import React from 'react';
import {
  Button,
  Confirmation,
  Drawer,
  Loader,
  MenuItemProps,
  Table,
  useToasts,
} from 'freemium-ui';
import { History } from 'history';
import { get, isEmpty, uniq } from 'lodash';
import qs from 'query-string';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { CONTACT_LISTS_LIMIT, GLOBAL_PER_PAGE } from 'routes/constants';
import ElementInView from 'shared/components/ElementInView';
import Icon from 'shared/components/Icon';
import { Center, FreshLink } from 'shared/styles';
import {
  AccountInfoType,
  AssociationResponse,
  BreadcrumpDataType,
  ConfirmationType,
  ContactFormType,
  ContactListType,
  ContactListsResponseType,
  FormServFieldType,
} from 'shared/types';
import { useAccessControl } from 'utils/access-control';
import EmptyState from '../../../../shared/components/EmptyState';
import {
  BACK_END_FIELDS,
  COLUMNS_HIDE_ORDERING_KEY,
} from '../../../../shared/constants';
import CreatedBy from '../../../components/CreatedBy';
import HeaderComponent from '../../../components/HeaderComponent';
import NewListForm from '../../../components/NewButton/NewListForm';
import ListContactsCount from '../components/list-contacts-count';
import { useLocalStorage } from '../../../../hooks';
import {
  useCheckContactListAssociation,
  useDeleteContactList,
  useGetContactLists,
  useGetContactListsCount,
} from '../hooks';
import { getTableColumnsFromFields } from '../../../utils';
import {
  ConfirmationText,
  DisplayBox,
  TableDataContainer,
} from '../../../styles';
import { Association, AssociationsHeading, AssociationsList } from '../styles';
import {
  AllCollectorChannels,
  getListsColumnSettings,
  listFields,
} from '../constants';

type ListsProps = {
  contactForm: ContactFormType;
  setBreadcrumpData?: (
    str: BreadcrumpDataType | React.SetStateAction<BreadcrumpDataType>,
  ) => void;
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
  accountInfo?: AccountInfoType;
  contactsCount: number;
  fetchContactCount: () => void;
};

const Lists = (props: ListsProps) => {
  const { t } = useTranslation();
  const {
    push,
    location: { pathname },
  }: History = useHistory();
  const [, setToast] = useToasts();
  const { access } = useAccessControl();

  const {
    setBreadcrumpData,
    contactForm,
    setConfirmationData,
    accountInfo,
    contactsCount,
    fetchContactCount,
  } = props;
  const fields = React.useMemo(
    () =>
      contactForm?.fields?.filter(
        (contact) => !BACK_END_FIELDS.includes(contact.name),
      ) || [],
    [contactForm],
  );
  const columnSettings = React.useMemo(
    () => getListsColumnSettings({ accountInfo }),
    [accountInfo],
  );

  const [count, setCount] = React.useState<number>(0);
  const [pageToken, setPageToken] = React.useState<any>({ 0: '' });
  const [pageIndex, setPageIndex] = React.useState<number>(0);
  const [contactListToDelete, setContactListToDelete] =
    React.useState<ContactListType>();
  const [viewAllAssociations, setViewAllAssociations] =
    React.useState<boolean>(false);
  const [associations, setAssociations] = React.useState<AssociationResponse>(
    {},
  );
  const [columnOrder, setColumnOrder] = useLocalStorage(
    'lists-column-order',
    uniq(
      listFields.map(
        (field: FormServFieldType) => field[COLUMNS_HIDE_ORDERING_KEY],
      ),
    ),
  );
  const [hiddenColumns, setHiddenColumns] = useLocalStorage(
    'lists-hidden-columns',
    listFields
      .filter(
        (field: FormServFieldType) =>
          !(
            columnSettings[field.name]?.showInitial ||
            columnSettings[field.name]?.disableReordering
          ),
      )
      .map((field) => field[COLUMNS_HIDE_ORDERING_KEY]),
  );
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  const [editData, setEditData] = React.useState<
    ContactListType & {
      clone?: boolean;
    }
  >({});

  const {
    data: contactListsData,
    isLoading: isLoadingContactLists,
    isFetching: isFetchingContactLists,
    refetch: refetchContactLists,
    isError: isContactListsError,
  } = useGetContactLists({
    page_token: pageToken[pageIndex] || '',
    callback: (isSuccess: boolean, response: ContactListsResponseType) => {
      if (isSuccess) {
        const nextURL = get(response, ['_links', 'next', 'href'], '');
        if (nextURL) {
          const parseData: any = qs.parseUrl(nextURL);
          if (parseData.query?.next_token) {
            setPageToken((pgTkn) => ({
              ...pgTkn,
              [pageIndex + 1]: parseData.query.next_token,
            }));
          }
        }
      }
    },
  });
  const { contactList: contactLists = [] } = contactListsData || {};

  const rowMenuOptions = React.useMemo<MenuItemProps[]>(() => {
    const rowOptions: MenuItemProps[] = [];
    if ((access || []).indexOf('contact:write') > -1) {
      rowOptions.push(
        ...[
          {
            id: 'edit',
            name: `${t('edit')}`,
          },
          {
            id: 'delete',
            name: `${t('delete')}`,
          },
          {
            id: 'duplicate',
            name: `${t('duplicate')}`,
            disabled:
              count >=
              (Number(
                accountInfo?.resource_limit_configuration?.contact_lists,
              ) || CONTACT_LISTS_LIMIT),
          },
        ],
      );
    }
    if ((access || []).indexOf('contact:read') > -1) {
      rowOptions.push({
        id: 'viewContacts',
        name: `${t('view_contacts')}`,
      });
    }
    return rowOptions;
  }, [access, count]);

  const {
    data: contactListsCountData,
    isLoading: isLoadingContactListsCount,
    isFetching: isFetchingContactListsCount,
  } = useGetContactListsCount({});

  const { mutate: deleteContactList, isLoading: isDeletingContactList } =
    useDeleteContactList({
      callback: (success: boolean) => {
        if (success) {
          setViewAllAssociations(false);
          setContactListToDelete(undefined);
          setAssociations({});
          setConfirmationData({});
        }
      },
      setToast,
    });

  const {
    mutate: checkContactListAssociation,
    isLoading: isLoadingAssociations,
  } = useCheckContactListAssociation({
    callback: (
      success: boolean,
      response: AssociationResponse,
      variables: ContactListType,
    ) => {
      if (success) {
        if (Object.keys(response).length) {
          setContactListToDelete(variables);
          setAssociations(response);
        } else {
          setContactListToDelete(undefined);
          setAssociations({});
          setViewAllAssociations(false);
          setConfirmationData({
            id: new Date().toISOString(),
            title: `${t('confirm_deletion')}`,
            className: 'error',
            data: variables,
            fill: '#d7461f',
            confirmButtonText: `${t('delete')}`,
            onConfirm: () => {
              setConfirmationData((old) => ({
                ...old,
                confirmButtonDisabled: true,
              }));
              deleteContactList(variables);
            },
            infoText: `${t('contacts.menu.delete.infotext')}`,
            description: (
              <Trans
                i18nKey="contacts.menu.delete.list_desc"
                values={{
                  list: variables.label,
                }}
                components={{ bold: <b /> }}
              />
            ),
            glyph: 'info',
          });
        }
      } else {
        setContactListToDelete(undefined);
        setAssociations({});
        setToast({
          type: 'error',
          delay: 3000,
          text: `${t('forms.toast.error.contact_list_deletion')}`,
        });
      }
    },
    setToast,
  });

  const columns = getTableColumnsFromFields({
    fields: listFields.map((eList) => ({
      ...eList,
      label: `${t(eList.label || '')}`,
    })),
    contactLists: contactLists || [],
    columnSettings,
    accountInfo,
    skipFields: ['no_of_contacts', 'created_by_id'],
  }).map((column) => {
    const newColumn = { ...column };
    if (newColumn.id === 'no_of_contacts') {
      newColumn.accessor = (cl: ContactListType) => (
        <ElementInView>
          <ListContactsCount
            enabled={isEmpty(editData)}
            id={cl.display_id || ''}
          />
        </ElementInView>
      );
    }
    if (newColumn.id === 'created_by_id') {
      newColumn.accessor = (cl: ContactListType) => (
        <ElementInView>
          <CreatedBy userId={cl.created_by_id || ''} />
        </ElementInView>
      );
    }
    if (newColumn?.id === 'updated_by_id') {
      newColumn.accessor = (cl: any) => {
        console.log(cl.created_by_id, cl.updated_by_id);
        return (
          <ElementInView>
            <CreatedBy userId={cl.updated_by_id || cl.created_by_id || ''} />
          </ElementInView>
        );
      };
    }
    return newColumn;
  });

  const allAssociations = Object.keys(associations);

  React.useEffect(() => {
    refetchContactLists();
  }, [pageIndex]);

  React.useEffect(() => {
    if (typeof contactListsCountData === 'number') {
      if (setBreadcrumpData) {
        setBreadcrumpData((old) => ({
          ...old,
          count: contactListsCountData,
        }));
      }
      setCount(contactListsCountData);
      if (window && window.heap) {
        window.heap.addUserProperties({
          lists_count: contactListsCountData,
        });
      }
    }
  }, [contactListsCountData]);

  if (isContactListsError)
    return <Center>{t('empty_state.message.network_error')}</Center>;

  if (isLoadingContactLists || isLoadingContactListsCount)
    return (
      <Center>
        <Loader />
      </Center>
    );

  return (
    <TableDataContainer className="lists-container">
      <Table
        data={contactLists || []}
        columns={columns}
        isFetching={isFetchingContactLists || isFetchingContactListsCount}
        isLoading={!contactListsData && !isContactListsError}
        count={count}
        loaderStyles={{ minHeight: 'calc(100vh - 136px)' }}
        pageIndex={pageIndex}
        pageSize={GLOBAL_PER_PAGE}
        hiddenColumns={hiddenColumns}
        columnOrder={columnOrder}
        disableRowSelect
        hideFilters
        onNextPageClick={() => setPageIndex(pageIndex + 1)}
        onPreviousPageClick={() => setPageIndex(pageIndex - 1)}
        enableNextPage={!!contactListsData?._links?.next}
        enablePreviousPage={pageIndex > 0}
        hideTableOnEmpty
        dataKey="display_id"
        hideBottomPagination
        cellPropGetter={(cell: any) => ({
          className: cell.column.id,
        })}
        onRowClick={(row: any) => {
          push(`${pathname}/${get(row, ['original', 'display_id'], '')}`);
        }}
        onMenuOptionSelect={(option: string, rowData: ContactListType) => {
          switch (option) {
            case 'viewContacts':
              push(`${pathname}/${rowData.display_id}`);
              break;
            case 'edit':
              setEditData(rowData);
              break;
            case 'delete':
              checkContactListAssociation(rowData);
              break;
            case 'duplicate':
              setEditData({
                ...rowData,
                label: rowData.label
                  ? `${t('copy_of', {
                      label: rowData.label || '',
                    })}`
                  : '',
                clone: true,
              });
              break;
          }
        }}
        headerComponent={
          <HeaderComponent
            type="list"
            columnOrder={columnOrder}
            setConfirmationData={setConfirmationData}
            setColumnOrder={(cols: string[]) => setColumnOrder(cols)}
            hiddenColumns={hiddenColumns}
            setHiddenColumns={(cols: string[]) => setHiddenColumns(cols)}
            fields={fields}
            hideExportColumns
            contactLists={contactLists || []}
            contactListsCount={count}
            columnSettings={columnSettings}
            showNewList
            columns={listFields.map((eField) => ({
              ...eField,
              label: `${t(eField.label || '')}`,
            }))}
            accountInfo={accountInfo}
            executeSegment={() => {
              fetchContactCount();
            }}
            contactsCount={contactsCount}
          />
        }
        emptyComponent={
          <EmptyState
            minHeight="calc(100vh - 204px)"
            title={`${t('empty_state.title.no_contacts_to_display')}`}
            message={`${t('empty_state.message.click_to_add_lists')}`}
            imgSrc="empty-state-contacts.svg"
          >
            {(access || []).indexOf('contact:write') > -1 && (
              <Button inline onClick={() => push('/lists?open=list')}>
                {`+ ${t('new_list')}`}
              </Button>
            )}
          </EmptyState>
        }
        rowMenuOptions={() => rowMenuOptions}
      />
      <Drawer
        isOpen={Boolean(editData.display_id)}
        isFullHeight
        clickOutToClose={false}
        placement="right"
        onDismiss={() => {
          if (isDirty) {
            setConfirmationData({
              id: new Date().toISOString(),
              title: `${t('confirm_close')}`,
              onConfirm: () => {
                setEditData({});
                setConfirmationData({});
              },
              description: `${t('contacts.list_changes_not_saved')}`,
            });
          } else {
            setEditData({});
          }
        }}
      >
        <NewListForm
          setIsDirty={(d: boolean) => setIsDirty(d)}
          values={editData}
          contactLists={contactLists || []}
          toggleDrawer={() => setEditData({})}
        />
      </Drawer>
      <Confirmation
        width="540px"
        isOpen={!!contactListToDelete?.display_id}
        onDismiss={(type) => {
          if (type === 'overlay') return;
          setContactListToDelete(undefined);
          setConfirmationData({});
        }}
        title={`${t('forms.confirmation.delete_list')}`}
        cancelButtonDisabled={isLoadingAssociations || isDeletingContactList}
        confirmButtonDisabled={isLoadingAssociations || isDeletingContactList}
        cancelButtonText={`${t('cancel')}`}
        confirmButtonText={`${t('delete')}`}
        onConfirm={() => {
          if (contactListToDelete) deleteContactList(contactListToDelete);
          else {
            setContactListToDelete(undefined);
            setConfirmationData({});
          }
        }}
      >
        <ConfirmationText>
          {t('forms.confirmation.confirm_delete_list', {
            label: contactListToDelete?.label,
          })}
        </ConfirmationText>
        <DisplayBox className="error">
          <div>
            <Icon fill="#d7461f" width="16" height="16" glyph="info" />
          </div>
          <div className="divider" />
          <div className="text-content">
            <Trans i18nKey="lists.desc" components={{ bold: <b /> }} />
          </div>
        </DisplayBox>
        <AssociationsHeading>
          <span className="a-heading">{`Associated (${allAssociations.length})`}</span>
          {allAssociations.length > 2 && (
            <FreshLink
              onClick={() => {
                setViewAllAssociations((old) => !old);
              }}
            >
              {viewAllAssociations ? `${t('view_less')}` : `${t('view_all')}`}
            </FreshLink>
          )}
        </AssociationsHeading>
        <AssociationsList>
          {allAssociations
            .slice(0, viewAllAssociations ? undefined : 2)
            .map((surveyName) => (
              <Association key={surveyName}>
                <div className="a-name">{surveyName}</div>
                <div className="a-channel-list">
                  {associations[surveyName].map((channel) => (
                    <div key={channel} className="channel-chip">
                      {AllCollectorChannels[channel]}
                    </div>
                  ))}
                </div>
              </Association>
            ))}
        </AssociationsList>
      </Confirmation>
    </TableDataContainer>
  );
};

export default Lists;
